import { useState, useEffect } from 'react';
import {
  TextInput,
  Button,
  Stack,
  Paper,
  Group,
  Title,
  Center,
  MultiSelect,
  Select,
  Textarea,
  Box,
  FileInput,
  Autocomplete,
  Tooltip,
  Text,
  Loader,
  Grid,
  Card,
  ActionIcon,
  Modal,
  Badge,
} from '@mantine/core';
import { useAuth0 } from '@auth0/auth0-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { IconCheck, IconInfoCircle, IconX, IconEdit, IconTrash } from '@tabler/icons-react';
import { styles } from './styles';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';

import { updateLawyer, deleteLawyer } from '../ViewLawyers/fetches';
import {
  fetchLawyer,
  fetchLawyerImage,
  updateImage,
  resummariseLawyer,
  deleteLawyerImage,
  updateLawyerName,
} from '../../services/lawyerFetches';
import BasicTable from '../../components/Tables/BasicTable';
import { fetchExpertise } from '../../services/proposalsRoutes';

interface Lawyer {
  LawyerName: string;
  image?: File | null;
  Bio: string;
  Docs: any[];
  Expertise: string[];
  PositionEn: string;
  PositionPl: string;
  ProfessionalTitleEn: string | null;
  ProfessionalTitlePl: string | null;
  email?: string;
  phone?: string;
  last_edited?: Date;
  location?: string;
  ImageLocationS3?: string;
}

export default function EditLawyer() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const [deleteImageModalOpened, { open: openDeleteImageModal, close: closeDeleteImageModal }] =
    useDisclosure(false);
  const [deleteLawyerModalOpened, { open: openDeleteLawyerModal, close: closeDeleteLawyerModal }] =
    useDisclosure(false);

  const [lawyerData, setLawyerData] = useState<Lawyer>({
    LawyerName: '',
    Bio: '',
    Docs: [],
    Expertise: [],
    PositionEn: '',
    PositionPl: '',
    ProfessionalTitleEn: '',
    ProfessionalTitlePl: '',
    email: '',
    phone: '',
    last_edited: undefined,
    location: '',
  });

  // Name editing state
  const [isEditingName, setIsEditingName] = useState(false);
  const [tempName, setTempName] = useState('');

  const [image, setImage] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [expertiseList, setExpertiseList] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState<boolean>(false);
  const [bioPL, setBioPL] = useState<string>("");

  // Helper to safely extract a string from Bio which can sometimes be an object
  const getBioString = (bioValue: unknown): string => {
    if (typeof bioValue === 'string') return bioValue;
    if (bioValue && typeof bioValue === 'object') {
      const obj = bioValue as any;
      const content = typeof obj.Content === 'string' ? obj.Content : '';
      const contentPL = typeof obj.ContentPL === 'string' ? obj.ContentPL : '';
      return content && content.length > 0 ? content : contentPL;
    }
    return '';
  };

  // Derived validation state for resummarise requirements
  const bioString = getBioString(lawyerData.Bio as unknown);
  const isResummariseReady = ((bioString.trim().length > 0) || (bioPL.trim().length > 0)) && ((lawyerData.Expertise?.length ?? 0) > 0);
  const isBioMissing = (bioString.trim().length === 0) && (bioPL.trim().length === 0);
  const isExpertiseMissing = (lawyerData.Expertise?.length ?? 0) === 0;
  const isPlaceholderImage = (lawyerData.ImageLocationS3 || '').endsWith('WKB Lawyers – Team_files/placeholder.jpg');

  // Store original data to compare for changes
  const [originalData, setOriginalData] = useState<typeof lawyerData | null>(null);

  // Helper function to check if there are actual changes
  const hasChanges = (): boolean => {
    if (!originalData) return false;
    
    // Compare relevant fields
    return (
      originalData.LawyerName !== lawyerData.LawyerName ||
      originalData.Bio !== lawyerData.Bio ||
      // include PL bio changes
      (('BioPL' in (originalData as any) ? (originalData as any).BioPL : '') !== bioPL) ||
      JSON.stringify(originalData.Expertise) !== JSON.stringify(lawyerData.Expertise) ||
      originalData.PositionEn !== lawyerData.PositionEn ||
      originalData.PositionPl !== lawyerData.PositionPl ||
      originalData.ProfessionalTitleEn !== lawyerData.ProfessionalTitleEn ||
      originalData.ProfessionalTitlePl !== lawyerData.ProfessionalTitlePl ||
      originalData.email !== lawyerData.email ||
      originalData.phone !== lawyerData.phone ||
      originalData.location !== lawyerData.location
    );
  };

  // Helper function to check if image has changed
  const hasImageChanged = (): boolean => {
    // Treat only File (user-picked) as change; Blob from server is not a change
    return image instanceof File;
  };

  // Update page title when there are unsaved changes
  useEffect(() => {
    const hasUnsavedChanges = hasChanges() || hasImageChanged();
    if (hasUnsavedChanges) {
      document.title = `${lawyerData.LawyerName} - ${t('unsavedChanges')} | WKB`;
    } else {
      document.title = `${lawyerData.LawyerName} | WKB`;
    }
  }, [lawyerData, image, hasChanges, hasImageChanged, t]);

  useEffect(() => {
    const getLawyerInfo = async () => {
      try {
        const accessToken = await getAccessTokenSilently();
        if (id) {
          const response = await fetchLawyer(accessToken, id);
          const image = await fetchLawyerImage(accessToken, id);
          if (response) {
            // Normalize Bio to a string in case backend returns an object
            const normalized = {
              ...response,
              Bio: getBioString(response?.Bio as unknown),
            } as typeof lawyerData;
            setLawyerData(normalized);
            // If backend provides nested Bio, hydrate bioPL from ContentPL if present
            if (response && typeof response.Bio === 'object' && response.Bio && 'ContentPL' in (response as any).Bio) {
              setBioPL(((response as any).Bio.ContentPL as string) || "");
            } else if ((response as any).BioPL) {
              setBioPL((response as any).BioPL as string);
            }
            // @ts-ignore
            setImage(image);
            setOriginalData(normalized); // Set original data on load
          }
        }
      } catch (error) {
        console.error('Error fetching lawyer data:', error);
        notifications.show({
          title: t('error'),
          message: t('failedToFetchLawyer'),
          color: 'red',
          autoClose: 3000,
          position: 'top-right',
        });
      } finally {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    };
    getLawyerInfo();
  }, [getAccessTokenSilently, id]);

  useEffect(() => {
    async function getExpertise() {
      const accessToken = await getAccessTokenSilently();
      const response = await fetchExpertise(accessToken);
      setExpertiseList(response);
    }
    getExpertise();
  }, []);

  // Manage image URL creation and cleanup
  useEffect(() => {
    if (image) {
      // Create object URL for the new image
      const url = URL.createObjectURL(image);
      setImageUrl(url);
      
      // Cleanup function to revoke the object URL
      return () => {
        URL.revokeObjectURL(url);
      };
    } else {
      setImageUrl(null);
    }
  }, [image]);

  // Auto-save when image is uploaded
  useEffect(() => {
    const autoSaveImage = async () => {
      // Only auto-save if initial load is complete and image is a File (not a Blob from server)
      if (!initialLoadComplete || !image || !(image instanceof File) || !id) {
        return;
      }

      try {
        setLoading(true);
        let accessToken;
        try {
          accessToken = await getAccessTokenSilently();
        } catch (error) {
          console.error('Error getting access token:', error);
          notifications.show({
            title: t('error'),
            message: t('failedToUpdateLawyer'),
            color: 'red',
            autoClose: 3000,
            position: 'top-right',
          });
          return;
        }
        
        // Only save if there are actual changes
        if (hasChanges() || hasImageChanged()) {
          try {
            // Save lawyer data first
            const dataToSave = { ...lawyerData, Bio: bioString, BioPL: bioPL };
            await updateLawyer(accessToken, dataToSave);
            
            // Only proceed with image update if lawyer data was saved successfully
            if (image instanceof File) {
              await updateImage(accessToken, id, image);
              setLawyerData((prev) => ({
                ...prev,
                ImageLocationS3: `wkb-lawyer-profiles/WKB Lawyers – Team_files/${id}.jpg`,
              }));
            }
            
            // Update original data after successful save
            setOriginalData({ ...dataToSave } as typeof lawyerData);
            
            notifications.show({
              title: t('success'),
              message: t('lawyerInformationUpdated'),
              color: 'green',
              icon: <IconCheck size={18} />,
              autoClose: 2000,
              position: 'top-right',
              withCloseButton: false,
            });
            
            // Re-summarise the lawyer profile only if required fields are present
            if (isResummariseReady) {
              await resummariseLawyer(accessToken, id);
            }
          } catch (error) {
            // If any part of the save fails, show error and don't update original data
            console.error('Error saving lawyer data:', error);
            notifications.show({
              title: t('error'),
              message: t('failedToUpdateLawyer'),
              color: 'red',
              autoClose: 3000,
              position: 'top-right',
            });
            return; // Exit early, don't proceed with success flow
          }
        } else {
          console.log('No changes detected, skipping auto-save');
        }
      } catch (error) {
        console.error('Error auto-saving image:', error);
        notifications.show({
          title: t('error'),
          message: t('failedToUpdateLawyer'),
          color: 'red',
          autoClose: 3000,
          position: 'top-right',
        });
      } finally {
        setLoading(false);
      }
    };

    autoSaveImage();
  }, [image, initialLoadComplete, id, getAccessTokenSilently, t, isResummariseReady]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    if (name === 'BioPL') {
      setBioPL(value);
      return;
    }
    setLawyerData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleExpertiseChange = (values: string[]) => {
    setLawyerData((prevData) => ({ ...prevData, Expertise: values }));
  };

  const handleNameEdit = () => {
    setTempName(lawyerData.LawyerName);
    setIsEditingName(true);
  };

  const handleNameCancel = () => {
    setTempName('');
    setIsEditingName(false);
  };

  const handleNameSave = async () => {
    if (!tempName.trim() || tempName === lawyerData.LawyerName) {
      setIsEditingName(false);
      return;
    }

    try {
      setLoading(true);
      const accessToken = await getAccessTokenSilently();
      const result = await updateLawyerName(accessToken, lawyerData.LawyerName, tempName.trim());
      
      notifications.show({
        title: t('success'),
        message: t('lawyerNameUpdated'),
        color: 'green',
        icon: <IconCheck size={18} />,
        autoClose: 2000,
        position: 'top-right',
        withCloseButton: false,
      });

      // Navigate to the new URL with updated name
      navigate(`/editlawyer/${encodeURIComponent(tempName.trim())}`);
      
      // Update local state
      setLawyerData(prev => ({ ...prev, LawyerName: tempName.trim() }));
      
      // Update original data after successful name change
      setOriginalData(prev => prev ? { ...prev, LawyerName: tempName.trim() } : null);
      
      setIsEditingName(false);
    } catch (error: any) {
      console.error('Error updating lawyer name:', error);
      notifications.show({
        title: t('error'),
        message: error.response?.data?.error || t('failedToUpdateLawyer'),
        color: 'red',
        autoClose: 5000,
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Block save if required fields for resummarise are missing
    if (!isResummariseReady) {
      notifications.show({
        title: t('warning'),
        message: t('resummariseRequirements'),
        color: 'yellow',
        autoClose: 4000,
        position: 'top-right',
      });
      return;
    }

    // Check if there are actual changes before saving
    if (!hasChanges() && !hasImageChanged()) {
      notifications.show({
        title: t('info'),
        message: t('noChangesToSave'),
        color: 'blue',
        autoClose: 2000,
        position: 'top-right',
        withCloseButton: false,
      });
      return;
    }

    let accessToken;
    try {
      accessToken = await getAccessTokenSilently();
    } catch (error) {
      console.error('Error getting access token:', error);
      notifications.show({
        title: t('error'),
        message: t('failedToUpdateLawyer'),
        color: 'red',
        autoClose: 3000,
        position: 'top-right',
      });
      return;
    }
    
    if (id) {
      try {
        // Prepare data for backend: EN Bio is in lawyerData.Bio, PL Bio is in bioPL
        const dataToSave = {
          ...lawyerData,
          Bio: bioString, // EN Bio
          BioPL: bioPL, // PL Bio
        };

        // Save lawyer data first
        await updateLawyer(accessToken, dataToSave);
        
        // Only proceed with image update if lawyer data was saved successfully
        if (image instanceof File) {
          await updateImage(accessToken, id, image);
          setLawyerData((prev) => ({
            ...prev,
            ImageLocationS3: `wkb-lawyer-profiles/WKB Lawyers – Team_files/${id}.jpg`,
          }));
        }
        
        // Update original data after successful save
        setOriginalData({ ...dataToSave });
        
        notifications.show({
          title: t('success'),
          message: t('lawyerInformationUpdated'),
          color: 'green',
          icon: <IconCheck size={18} />,
          autoClose: 1500,
          position: 'top-right',
          withCloseButton: false,
        });
        
        // Re-summarise the lawyer profile only if required fields are present
        if (isResummariseReady) {
          const originalAny = (originalData as any) || {};
          const englishChanged = (originalAny.Bio ?? '') !== (lawyerData.Bio ?? '');
          const expertiseChanged = JSON.stringify(originalAny.Expertise ?? []) !== JSON.stringify(lawyerData.Expertise ?? []);
          if (englishChanged || expertiseChanged) {
            await resummariseLawyer(accessToken, id);
          }
        }
      } catch (error) {
        // If any part of the save fails, show error and don't update original data
        console.error('Error saving lawyer data:', error);
        notifications.show({
          title: t('error'),
          message: t('failedToUpdateLawyer'),
          color: 'red',
          autoClose: 3000,
          position: 'top-right',
        });
        return; // Exit early, don't proceed with success flow
      }
    }
    //   navigate('/lawyers');
  };

  const handleDeleteImage = async () => {
    try {
      const accessToken = await getAccessTokenSilently();
      if (id) {
        setLoading(true);
        await deleteLawyerImage(accessToken, id);
        // After deletion, backend sets placeholder. Fetch it and display.
        const blob = await fetchLawyerImage(accessToken, id);
        if (blob) {
          // Use Blob, not File, so it does NOT count as a changed image
          // @ts-ignore
          setImage(blob);
          // Ensure UI knows we're on placeholder to hide delete button
          setLawyerData((prev) => ({
            ...prev,
            ImageLocationS3: 'wkb-lawyer-profiles/WKB Lawyers – Team_files/placeholder.jpg',
          }));
        } else {
          setImage(null);
          setImageUrl(null);
          setLawyerData((prev) => ({
            ...prev,
            ImageLocationS3: 'wkb-lawyer-profiles/WKB Lawyers – Team_files/placeholder.jpg',
          }));
        }
        notifications.show({
          title: t('success'),
          message: t('imageDeletedSuccessfully'),
          color: 'green',
          icon: <IconCheck size={18} />,
          autoClose: 1500,
          position: 'top-right',
          withCloseButton: false,
        });
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      notifications.show({
        title: t('error'),
        message: t('failedToDeleteImage'),
        color: 'red',
        autoClose: 3000,
        position: 'top-right',
      });
    } finally {
      setLoading(false);
      closeDeleteImageModal();
    }
  };

  const handleDeleteLawyer = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const accessToken = await getAccessTokenSilently();
      await deleteLawyer(accessToken, id);
      
      notifications.show({
        title: t('success'),
        message: t('lawyerDeletedSuccessfully'),
        color: 'green',
        icon: <IconCheck size={18} />,
        autoClose: 2000,
        position: 'top-right',
        withCloseButton: false,
      });

      // Navigate back to lawyers list
      navigate('/editlawyerinfo');
    } catch (error) {
      console.error('Error deleting lawyer:', error);
      notifications.show({
        title: t('error'),
        message: t('failedToDeleteLawyer'),
        color: 'red',
        autoClose: 5000,
        position: 'top-right',
      });
    } finally {
      setLoading(false);
      closeDeleteLawyerModal();
    }
  };

  if (loading) {
    return (
      <Center style={{ height: '100vh' }}>
        <Title>{t('loading')}</Title>
      </Center>
    );
  }

  return (
    // <Paper w="90%" h="90vh" shadow="sm" withBorder radius="md">
    <>
      <Group mt="lg" ml="xl" style={{ alignSelf: 'flex-start' }}>
        {isEditingName ? (
          <Group gap="xs">
            <TextInput
              value={tempName}
              onChange={(e) => setTempName(e.currentTarget.value)}
              placeholder={t('lawyerName')}
              style={{ minWidth: '250px' }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleNameSave();
                } else if (e.key === 'Escape') {
                  handleNameCancel();
                }
              }}
              autoFocus
            />
            <ActionIcon
              color="green"
              onClick={handleNameSave}
              disabled={loading}
            >
              <IconCheck size={16} />
            </ActionIcon>
            <ActionIcon
              color="red"
              onClick={handleNameCancel}
              disabled={loading}
            >
              <IconX size={16} />
            </ActionIcon>
          </Group>
        ) : (
          <Group gap="xs">
            <Title order={2}>{lawyerData.LawyerName}</Title>
            {(hasChanges() || hasImageChanged()) && (
              <Badge color="orange" variant="light" size="sm">
                {t('unsavedChanges')}
              </Badge>
            )}
            <ActionIcon
              variant="subtle"
              onClick={handleNameEdit}
              disabled={loading}
            >
              <IconEdit size={16} />
            </ActionIcon>
          </Group>
        )}
      </Group>
      <form onSubmit={handleSubmit}>
        <div style={styles.gridContainer}>
          <Stack p="md" align="center" style={styles.item1}>
            <Box>
              {/* Hidden file input */}
              <Center>
                <FileInput
                  id="file-input"
                  style={{ display: 'none' }}
                  accept="image/*"
                  onChange={setImage}
                />
              </Center>

              {imageUrl && (
                <Center mb="sm">
                  {/* Container for image and overlay cross button */}
                  <Box
                    style={{
                      position: 'relative',
                      display: 'inline-block',
                    }}
                  >
                    <img
                      src={imageUrl}
                      alt="Selected"
                      style={{ maxWidth: '100%', maxHeight: '300px' }}
                    />
                    {!isPlaceholderImage && (
                      <ActionIcon
                        onClick={openDeleteImageModal}
                        disabled={loading}
                        style={{
                          position: 'absolute',
                          top: 10,
                          right: 10,
                          backgroundColor: 'rgba(255, 54, 54, 0.7)',
                          opacity: 0.3,
                          transition: 'opacity 0.3s ease',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
                        onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.3')}
                      >
                        {loading ? <Loader size="xs" /> : <IconX size={16} />}
                      </ActionIcon>
                    )}
                  </Box>
                </Center>
              )}

              {/* Delete Image Confirmation Modal */}
              <Modal
                opened={deleteImageModalOpened}
                onClose={closeDeleteImageModal}
                title={t('confirmDeletion')}
              >
                <div>{t('confirmDeleteImage')}</div>
                <Group justify="flex-end" mt="md">
                  <Button color="gray" onClick={closeDeleteImageModal}>
                    {t('cancel')}
                  </Button>
                  <Button color="red" onClick={handleDeleteImage}>
                    {t('confirm')}
                  </Button>
                </Group>
              </Modal>

              {/* Delete Lawyer Confirmation Modal */}
              <Modal
                opened={deleteLawyerModalOpened}
                onClose={closeDeleteLawyerModal}
                title={t('confirmDeletion')}
              >
                <div>{t('confirmDeleteLawyer', { name: lawyerData.LawyerName })}</div>
                <Text size="sm" c="dimmed" mt="sm">
                  {t('deleteLawyerWarning')}
                </Text>
                <Group justify="flex-end" mt="md">
                  <Button color="gray" onClick={closeDeleteLawyerModal}>
                    {t('cancel')}
                  </Button>
                  <Button color="red" onClick={handleDeleteLawyer} loading={loading}>
                    {t('confirm')}
                  </Button>
                </Group>
              </Modal>

              <Group justify="center">
                <Button
                  color="green"
                  onClick={() => document.getElementById('file-input')?.click()}
                >
                  {t('UploadProfileImage')}
                </Button>
              </Group>
            </Box>
            <Stack w="80%" align="stretch" miw="250px">
              <TextInput
                label={t('email')}
                name="email"
                value={lawyerData.email || ''}
                onChange={handleChange}
                type="email"
              />
              <div>
                <Text size="sm" fw={500} mb={0}>
                  {t('phoneNumber')}
                </Text>
                <PhoneInput
                  country={'pl'}
                  value={lawyerData.phone || ''}
                  onChange={(phone) => setLawyerData((prevData) => ({ ...prevData, phone }))}
                  inputStyle={{
                    width: '100%',
                    height: '42px',
                    borderRadius: '4px',
                    border: '1px solid #ced4da',
                    fontSize: '14px',
                    paddingLeft: '48px', // extra space for the country flag
                  }}
                  buttonStyle={{
                    border: 'none',
                    background: 'transparent',
                  }}
                  containerStyle={{ width: '100%' }}
                />
              </div>
              <Autocomplete
                label={t('location')}
                name="Location"
                data={[t('warsaw'), t('poznan')]}
                value={lawyerData.location}
                onChange={(value) =>
                  setLawyerData((prevData) => ({ ...prevData, location: value }))
                }
              />
            </Stack>
          </Stack>
          <Box p="md" style={{ ...styles.item2, ...styles.smallGridContainer }}>
            <Autocomplete
              label={t('professionalTitlePl')}
              data={[
                'adwokat',
                'adwokatka',
                'radca prawny',
                'prawnik',
                'aplikant adwokacki',
                'aplikantka adwokacka',
                'aplikant radcowski ',
                'aplikantka radcowska',
                'aplikant rzecznikowski',
                'doradca podatkowy',
                'doradca restrukturyzacyjny',
                'doktorant',
                'ukończona aplikacja legislacyjna',
              ]}
              name="Position"
              value={lawyerData.ProfessionalTitlePl || ''}
              onChange={(value) => {
                if (value) {
                  setLawyerData((prevData) => ({ ...prevData, ProfessionalTitlePl: value }));
                } else {
                  // @ts-ignore
                  setLawyerData((prevData) => ({ ...prevData, ProfessionalTitlePl: null }));
                }
              }}
              //   placeholder={t('selectPosition')}
              w="70%"
              miw="180px"
            />

            <Autocomplete
              label={t('positionPl')}
              data={[
                'Partner Zarządzający',
                'Partner',
                'Partner Międzynarodowy',
                'Starszy Prawnik Zarządzający',
                'Counsel',
                'Starszy Prawnik',
                'Prawnik',
                'Młodszy Prawnik',
                'Asystent Prawny',
              ]}
              name="Position"
              value={lawyerData.PositionPl || ''}
              onChange={(value) => {
                if (value) {
                  setLawyerData((prevData) => ({ ...prevData, PositionPl: value }));
                } else {
                  // @ts-ignore
                  setLawyerData((prevData) => ({ ...prevData, PositionPl: null }));
                }
              }}
              //   placeholder={t('selectPosition')}
              w="70%"
              miw="180px"
            />
            <Autocomplete
              label={t('professionalTitleEn')}
              data={[
                'advocate',
                'legal counsel',
                'lawyer',
                'trainee advocate',
                'trainee legal advisor',
                'trainee patent attorney',
                'tax advisor',
                'restructuring advisor',
                'doctoral researcher',
                'Completed legislative training',
              ]}
              name="Position"
              value={lawyerData.ProfessionalTitleEn || ''}
              onChange={(value) => {
                if (value) {
                  setLawyerData((prevData) => ({ ...prevData, ProfessionalTitleEn: value }));
                } else {
                  // @ts-ignore
                  setLawyerData((prevData) => ({ ...prevData, ProfessionalTitleEn: null }));
                }
              }}
              //   placeholder={t('selectProfessionalTitle')}
              w="70%"
              miw="180px"
            />
            <Autocomplete
              label={t('positionEn')}
              data={[
                'Managing Partner',
                'Partner',
                'International Partner',
                'Managing Associate',
                'Counsel',
                'Senior Associate',
                'Associate',
                'Junior Associate',
                'Paralegal',
              ]}
              name="Position"
              value={lawyerData.PositionEn || ''}
              onChange={(value) => {
                if (value) {
                  setLawyerData((prevData) => ({ ...prevData, PositionEn: value }));
                } else {
                  // @ts-ignore
                  setLawyerData((prevData) => ({ ...prevData, PositionEn: null }));
                }
              }}
              //   placeholder={t('selectProfessionalTitle')}
              w="70%"
              miw="180px"
            />
            <Box style={styles.sg5}>
              <MultiSelect
                label={t('expertise')}
                data={expertiseList}
                value={lawyerData.Expertise || []}
                onChange={handleExpertiseChange}
                placeholder={t('selectExpertise')}
                required
                error={isExpertiseMissing ? t('expertiseRequired') : undefined}
                style={styles.sg5}
                w="70%"
                miw="180px"
              />
            </Box>
          </Box>
          <Box p="md" style={styles.item3} maw="800px">
            <Textarea
              label={
                <Group gap={0} w="200px">
                  {t('bio')} (EN)
                  <Tooltip
                    label={
                      <Text
                        size="sm"
                        style={{
                          maxWidth: '200px',
                          whiteSpace: 'normal',
                        }}
                      >
                        {t('lawyerBiography')}
                      </Text>
                    }
                    withArrow
                    w="200px"
                  >
                    <IconInfoCircle
                      size={16}
                      style={{ marginLeft: 5 }}
                      color="var(--mantine-primary-color-filled)"
                    />
                  </Tooltip>
                </Group>
              }
              name="Bio"
              value={bioString}
              onChange={handleChange}
              placeholder={t('bioPlaceholder')}
              autosize
              minRows={3}
              maxRows={10}
              required
              error={isBioMissing ? t('bioRequired') : undefined}
            />
            <Textarea
              mt="md"
              label={
                <Group gap={0} w="200px">
                  {t('bio')} (PL)
                </Group>
              }
              name="BioPL"
              value={bioPL}
              onChange={handleChange}
              placeholder={t('bioPlaceholderPL')}
              autosize
              minRows={3}
              maxRows={10}
            />
            <Group style={{ justifyContent: 'space-between' }} mt="lg">
              <Button
                color="red"
                variant="light"
                leftSection={<IconTrash size={18} />}
                size="md"
                onClick={openDeleteLawyerModal}
              >
                {t('deleteLawyer')}
              </Button>
              <Tooltip
                label={
                  !isResummariseReady 
                    ? t('resummariseRequirements') 
                    : (!hasChanges() && !hasImageChanged()) 
                      ? t('noChangesToSave')
                      : t('save')
                }
                disabled={isResummariseReady && (hasChanges() || hasImageChanged())}
              >
                <Button
                  type="submit"
                  color="green"
                  variant="light"
                  leftSection={<IconCheck size={18} />}
                  size="md"
                  disabled={!isResummariseReady || loading || (!hasChanges() && !hasImageChanged())}
                >
                  {t('save')}
                </Button>
              </Tooltip>
            </Group>
          </Box>
        </div>
      </form>
      <Group ml="xl">
        <h2>{t('relevantDocuments')}</h2>
        <Tooltip label={t('describeLawyerDocuments')} withArrow multiline w={220} position="right">
          <IconInfoCircle size={20} color="var(--mantine-primary-color-filled)" />
        </Tooltip>
      </Group>
      <BasicTable colLabels={{ Doc: 'doc', Date: 'date' }} data={lawyerData.Docs} />
    </>
    // </Paper>
  );
}
